import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase, handleSupabaseError } from "@/lib/hooks/api/utils";
import type { Contact_TasksUpdateInput } from "@/src/gql/graphql";
import { Contact_TasksUpdateInputSchema } from "@/src/gql/validation";

export function useCreateContactTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskData: {
      title: string;
      contact_id: string;
      description?: string;
      status?: string;
      priority?: string;
      due_date?: string;
      assigned_to?: string;
      tags?: string[];
    }) => {
      const supabase = getSupabase();
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error("User not authenticated");
      }

      const normalizedAssignedTo =
        taskData.assigned_to && taskData.assigned_to.toLowerCase() === "me"
          ? user.id
          : taskData.assigned_to;

      const { data, error } = await supabase
        .from("contact_tasks")
        .insert([
          {
            ...taskData,
            assigned_to: normalizedAssignedTo,
            user_id: user.id,
            status: taskData.status || "pending",
            priority: taskData.priority || "medium",
          },
        ])
        .select()
        .single();

      if (error) {
        console.error("Error creating contact task:", error);
        handleSupabaseError(error, "create contact task");
      }

      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["contact-tasks"] });
    },
    onError: (error) => {
      console.error("Error creating contact task:", error);
    },
  });
}

export function useUpdateContactTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Contact_TasksUpdateInput }) => {
      const validatedData = Contact_TasksUpdateInputSchema.parse(updates);

      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("contact_tasks")
        .update(validatedData)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("Error updating contact task:", error);
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["contact-tasks"] });
      void queryClient.invalidateQueries({ queryKey: ["contact-tasks", data.id] });
      toast.success("Task updated successfully");
    },
    onError: (error) => {
      toast.error("Failed to update task");
      console.error("Error updating contact task:", error);
    },
  });
}

export function useDeleteContactTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const supabase = getSupabase();
      const { error } = await supabase.from("contact_tasks").delete().eq("id", id);

      if (error) {
        console.error("Error deleting contact task:", error);
        throw new Error(error.message);
      }

      return { id, deleted: true };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["contact-tasks"] });
      toast.success("Task deleted successfully");
    },
    onError: (error) => {
      toast.error("Failed to delete task");
      console.error("Error deleting contact task:", error);
    },
  });
}
